import React from 'react';
import Modal, { ModalProps } from '@app/component/overlays/modal/Modal';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import { AbstractButton } from '@app/site/_form/button/AbstractButton';
import useAppForm from '@app/site/_form';
import PricingIntegrationInput from '@app/model/pricing-automation/input/PricingIntegrationInput';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@app/component/translation/Translation';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import {
	createPricingIntegrationDefaultValues,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/recommmendation/overlay/PricingIntegrationModal.utilities';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import Utilities from '@util/Utilities';
import { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';
import PricingArchitecture from '@app/model/pricing-automation/PricingArchitecture';
import AvailabilityDailyStateProvider from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/AvailabillityDailyStateProvider';
import SubmitPricingResultOutput from '@app/model/pricing-automation/output/SubmitPricingResultOutput';
import SubmitMonthlyPricingResultOutputState from '@app/model/pricing-automation/output/SubmitMonthlyPricingResultOutputState';
import PriceSubmissionBlockedItems from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/PriceSubmissionBlockedItems';
import Message from '@app/component/message/Message';
import { useNavigate } from 'react-router-dom';
import BulkAdjustmentBar from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/bulk-adjustment/BulkAdjustmentBar';
import {
	applyBulkAdjustment,
	BulkAdjustmentType,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/bulk-adjustment/BulkAdjustment.utilities';
import PriceEditRoomsTable from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/price-edit/PriceEditRoomsTable';
import { createRecommendationPriceEditGroups } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/price-edit/PriceEditGroups';

type PricingIntegrationModalProps = {
	companyHotel: CompanyHotel
	recommendationStates: PriceRecommendationState[],
	architectures: PricingArchitecture[],
	statDate: string,
	onSubmit: () => void
} & Omit<ModalProps, 'title' | 'width'>

const PricingIntegrationModal = React.memo(
	function PricingIntegrationModal({
																		companyHotel,
																		recommendationStates,
																		architectures,
																		statDate,
																		onSubmit,
																		...props
																	}: PricingIntegrationModalProps) {

		const toaster = useToaster();
		const { t } = useTranslation();
		const tools = useMarketStateTools();
		const [submissionResult, setSubmissionResult] = React.useState<SubmitPricingResultOutput | null>(null);
		const [derivativeSynchronizationSuppressionEpoch, setDerivativeSynchronizationSuppressionEpoch] = React.useState(0);
		const [bulkResetEpoch, setBulkResetEpoch] = React.useState(0);
		const navigate = useNavigate();
		const submissionError = submissionResult?.errorCode ?? submissionResult?.errorMessage;


		const defaultValues: PricingIntegrationInput = {
			...createPricingIntegrationDefaultValues(statDate, recommendationStates),
		};

		const queryClient = useQueryClient();
		const submitPricingMutation = useMutation({
			mutationFn: PricingAutomationService.submitPricing,
			onMutate: () => setSubmissionResult(null),
			onSuccess: result => {
				if (result.state === SubmitMonthlyPricingResultOutputState.FAILED) {
					setSubmissionResult(result);
					return;
				}
				queryClient.invalidateQueries({
					queryKey: PricingAutomationQueryKeys.pricingSubmissions(companyHotel.id),
				}).then(() => {
					toaster.success('Pricing', 'Pricing submitted successfully!');
					tools.reloadMarketStateForDay(statDate);
					props.ref.current?.close();
					onSubmit();
				});
			},
			onError: (e: string) => toaster.error('Pricing', t(e)),
		});


		const form = useAppForm({
			defaultValues: defaultValues,
			onSubmit: async ({ value }) => submitPricingMutation.mutateAsync({
				companyHotelId: companyHotel.id,
				input: value,
			}).catch(() => undefined),
		});

		// The Bulk Adjustment: every New Rate becomes the Current Rate plus the change. The cascade
		// from the master room stays off for this change, so each room keeps its own result.
		const applyBulk = (adjustmentType: BulkAdjustmentType, adjustmentValue: number) => {
			setDerivativeSynchronizationSuppressionEpoch(epoch => epoch + 1);

			recommendationStates.forEach((recommendationState, rwaIndex) => {
				recommendationState.minimumBasePriceRoomStates.forEach((room, roomIndex) => {
					const adjustedPrice = applyBulkAdjustment(Number(room.basePrice), adjustmentType, adjustmentValue);
					if (adjustedPrice === undefined) return;
					form.setFieldValue(
						`rates[${rwaIndex}].roomPrices[${roomIndex}].submittedPrice`,
						adjustedPrice,
					);
				});
			});
		};

		const reset = () => {
			setDerivativeSynchronizationSuppressionEpoch(epoch => epoch + 1);
			setBulkResetEpoch(epoch => epoch + 1);
			form.reset();
		};

		const groups = createRecommendationPriceEditGroups(recommendationStates, architectures);

		if (groups === undefined) return <Modal
			title={`Edit Recommended Rates - ${Utilities.formatDateString(statDate, 'DD MMMM YYYY', false)}`}
			width="2xlarge" {...props}>
			<Message state="error">Room configuration has changed. Reload the page before editing these recommendations.</Message>
		</Modal>;

		return <form.AppForm>
			<form onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}>
				<Modal
					title={`Edit Recommended Rates - ${Utilities.formatDateString(statDate, 'DD MMMM YYYY', false)}`}
					width="2xlarge" {...props} actions={<>
					<AbstractButton state="text" onClick={reset}>
						Reset
					</AbstractButton>
					<form.SubmitButton state="primary">
						Submit
					</form.SubmitButton>
				</>
				}>
					<p className="mb-2 text-sm text-gray-600">
						Adjust the recommended rates for the rooms below and submit your changes to update the rates.
					</p>

					{submissionResult !== null && submissionResult.blockedItems.length > 0 ? <PriceSubmissionBlockedItems
						items={submissionResult.blockedItems}
						currency={companyHotel.settings.main.currency}
						onDateSelect={selectedStatDate => {
							props.ref.current?.close();
							navigate('/secured/rateshopper/rates/market/calendar', { state: { selectedStatDate } });
						}} /> : null}
					{submissionResult && submissionResult.blockedItems.length === 0 && submissionError !== undefined && submissionError !== '' &&
						<Message state="error">{t(submissionError, ...(submissionResult.errorParams ?? []))}</Message>}

					<BulkAdjustmentBar currency={companyHotel.settings.main.currency}
					onApply={applyBulk}
					resetEpoch={bulkResetEpoch} />

					<AvailabilityDailyStateProvider companyHotel={companyHotel} statDate={statDate}>
						<PriceEditRoomsTable title="Rates Changes Summary"
						currency={companyHotel.settings.main.currency}
						groups={groups}
						form={form}
						suppressionEpoch={derivativeSynchronizationSuppressionEpoch} />
					</AvailabilityDailyStateProvider>
				</Modal>
			</form>
		</form.AppForm>;
	});

export default PricingIntegrationModal;
